import React from 'react';
import { FormRenderProps } from 'react-final-form';
import { Button, Form, FormError, FormField, FormProps } from '@gik/ui';
import { FormApi } from 'final-form';
import bemBlock from '@gik/core/utils/bemBlock';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { IProvideMyselfClaimDetails } from '@gik/calendar/models/ProvideMyselfClaimDetails';
import { ICalendarEntry } from '@gik/calendar/models/Calendar';
import noop from '@gik/core/utils/noop';
import { CalendarClaimFooter } from '../../CalendarClaimFooter';
import { IClaimFormValues } from '../../CalendarClaimEvent';
import { CalendarEventTypeIds, CalendarServiceCategoryIds } from '@gik/calendar/models/CalendarEventTypes';
import { getCalendarClaimFormSchema } from '../schema';
import { useInkindStore } from '@gik/inkind/store/InkindStore';
import { useInkind } from '@gik/api/inkinds/inkind';
import { inkindPageOwnerIsRecipient } from '@gik/inkind-page/utils/inkindPagePermissions';

export interface ProvideMyselfClaimFormProps extends Omit<FormProps, 'onSubmit'> {
  errorMessage?: React.ReactNode;

  entry?: ICalendarEntry;
  saving?: boolean;
  editEventButton?: boolean;
  unclaimButton?: boolean;
  backButton?: boolean;

  buttonsPortal?: () => HTMLElement;
  onSubmit?: (values: IClaimFormValues, form: FormApi) => void | Promise<void>;
  onBack?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  onUnclaim?: () => void | Promise<void>;
  onEditEvent?: () => void | Promise<void>;
}

export interface IProvideMyselfClaimFormValues extends IClaimFormValues {}

export function ProvideMyselfClaimForm({
  className,
  errorMessage,
  entry,
  editEventButton,
  unclaimButton,
  backButton,
  saving,
  buttonsPortal,
  onBack = noop,
  onCancel = noop,
  onUnclaim = noop,
  onEditEvent = noop,
  ...otherProps
}: ProvideMyselfClaimFormProps): React.ReactElement {
  const bem = bemBlock('provide-myself-claim-form');
  const formId = 'ProvideMyselfClaimForm';

  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkind } = useInkind(inkindRouteId);

  const claim = entry.claim as IProvideMyselfClaimDetails;
  const isEditing = !!claim;

  return (
    <Form
      key={JSON.stringify(saving)}
      schema={getCalendarClaimFormSchema(entry, CalendarServiceCategoryIds.ProvideMyself)}
      initialValues={{
        carType: claim?.carType,
        description: claim?.description,
        note: claim?.note,
      }}
      vertical
      className={bem(null, null, className)}
      id={formId}
      render={(form: FormRenderProps) => {
        let _buttons;

        const fieldsDisabled = form.submitting;
        const isSubmitting = form.submitting || saving;

        if (isEditing) {
          _buttons = (
            <CalendarClaimFooter
              form={form}
              editEventButton={editEventButton}
              unclaimButton={unclaimButton}
              onEditEvent={onEditEvent}
              onUnclaim={onUnclaim}
              backButton={backButton}
              onBack={onBack}
            >
              <Button wide type="submit" key="save" form={formId} disabled={!form.valid} loading={isSubmitting}>
                Save
              </Button>
            </CalendarClaimFooter>
          );
        } else {
          _buttons = (
            <CalendarClaimFooter onCancel={onCancel} backButton onBack={onBack}>
              <Button type="submit" wide form={formId} disabled={!form.valid} loading={isSubmitting}>
                CONFIRM
              </Button>
            </CalendarClaimFooter>
          );
        }

        const showNotetoRecipient = inkindPageOwnerIsRecipient(inkind);

        return (
          <>
            {entry.typeId === CalendarEventTypeIds.Transportation && (
              <FormField disabled={fieldsDisabled} name="carType" />
            )}
            <FormField disabled={fieldsDisabled} name="description" />

            {showNotetoRecipient && <FormField disabled={fieldsDisabled} name="note" />}

            {errorMessage && <FormError message={errorMessage} centered />}

            {renderPortal(_buttons, buttonsPortal)}
          </>
        );
      }}
      {...otherProps}
    />
  );
}
